import debug from 'debug';

import { decompressSessionStorage } from './sessionStorage';

const log = debug('rois:actions:excluded-vehicles');

/**
 * ⚠️ SINCRONIZACIÓN BIDIRECCIONAL - Sistema de Exclusiones
 *
 * Gestiona vehículos que el usuario eliminó EXPLÍCITAMENTE de workVehicles.
 * Estos vehículos NO deben volver a aparecer automáticamente aunque tengan conductor asignado.
 *
 * Flujo:
 * 1. Usuario elimina vehículo de WorkEquipment → añadir a excludedVehicles
 * 2. Loader NO sincroniza vehículos que están en excludedVehicles
 * 3. Usuario vuelve a marcar el vehículo o a elegir su conductor en Operarios
 *    → quitar de excludedVehicles (lo hace `OperatorsSelectionTable`)
 *
 * 🔴 La lista es de UNA actuación (2026-10-12). Antes era una por asistente y
 * por pestaña, y nadie la borraba: el vehículo quitado en una actuación dejaba
 * de precargarse en TODAS las siguientes de la misma pestaña (en la APK, el
 * WebView vive días). Ahora se guarda con el id de la actuación en curso y, si
 * la actuación es otra, la lista no cuenta. Spec:
 * docs/superpowers/specs/2026-10-12-excluded-work-vehicles-design.md
 */
/**
 * 🔴 Las claves tienen forma de borrador (`new-*`) a propósito: el almacén
 * duradero del APK (`offline/nativeDurableStorage.ts`, `isDurableKey`) guarda
 * por FORMA lo que debe sobrevivir a que Android mate la app. Con el nombre
 * anterior (`rois:excluded-work-vehicles:*`) el borrador de Maquinaria volvía
 * al reabrir la app pero la lista no, y el vehículo quitado se precargaba otra
 * vez (2026-10-12).
 */
export const NEW_ACTION_EXCLUDED_VEHICLES_KEY = 'new-action-excluded-work-vehicles';
export const VALIDATION_EXCLUDED_VEHICLES_KEY = 'new-validation-excluded-work-vehicles';

/** Dónde vive el id de la actuación en curso de cada asistente. */
const NEW_ACTION_DATA_KEY = 'new-action-data';
const VALIDATION_ACTION_DATA_KEY = 'new-validation-action-data';

interface StoredExclusions {
  /** Actuación a la que pertenece la lista (`null` si aún no tenía id). */
  actionId: string | null;
  vehicleIds: string[];
}

const storageKeyFor = (isValidation: boolean) =>
  isValidation
    ? VALIDATION_EXCLUDED_VEHICLES_KEY
    : NEW_ACTION_EXCLUDED_VEHICLES_KEY;

/**
 * Id de la actuación en curso del asistente: `actionId` en la actuación nueva,
 * `id` en validación. Se lee con `decompressSessionStorage` porque el build lo
 * guarda comprimido. `null` si no hay actuación legible (p. ej., una actuación
 * nueva que todavía no se ha creado en el servidor).
 */
const currentActionId = (isValidation: boolean): string | null => {
  const raw = sessionStorage.getItem(
    isValidation ? VALIDATION_ACTION_DATA_KEY : NEW_ACTION_DATA_KEY
  );
  if (!raw) return null;
  try {
    const data = JSON.parse(decompressSessionStorage(raw) ?? 'null');
    const id = isValidation ? data?.id : data?.actionId;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
};

/**
 * La lista guardada, o `null` si no la hay o no se puede leer. El formato
 * viejo (un array suelto, sin dueño) se trata como si no hubiera lista: no se
 * sabe de qué actuación era.
 */
const readStored = (isValidation: boolean): StoredExclusions | null => {
  const raw = sessionStorage.getItem(storageKeyFor(isValidation));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      Array.isArray(parsed.vehicleIds)
    ) {
      return {
        actionId: typeof parsed.actionId === 'string' ? parsed.actionId : null,
        vehicleIds: parsed.vehicleIds.filter(
          (id: unknown): id is string => typeof id === 'string'
        ),
      };
    }
  } catch (error) {
    console.error('[ExcludedVehicles] Error parsing excluded vehicles:', error);
  }
  return null;
};

const writeStored = (isValidation: boolean, value: StoredExclusions) => {
  sessionStorage.setItem(storageKeyFor(isValidation), JSON.stringify(value));
};

/**
 * Obtener lista de vehículos excluidos de la actuación EN CURSO. La de otra
 * actuación no cuenta.
 */
export const getExcludedVehicles = (
  isValidation: boolean = false
): string[] => {
  const stored = readStored(isValidation);
  if (!stored) return [];
  return stored.actionId === currentActionId(isValidation)
    ? stored.vehicleIds
    : [];
};

/**
 * Añadir vehículo a la lista de exclusiones de la actuación en curso. Si lo
 * guardado era de otra actuación, se descarta y se empieza una lista nueva.
 */
export const addExcludedVehicle = (
  vehicleId: string,
  isValidation: boolean = false
): void => {
  const excluded = getExcludedVehicles(isValidation);
  if (excluded.includes(vehicleId)) return;
  writeStored(isValidation, {
    actionId: currentActionId(isValidation),
    vehicleIds: [...excluded, vehicleId],
  });
  log('vehículo %s excluido (%s)', vehicleId, isValidation ? 'validación' : 'nueva');
};

/**
 * Quitar vehículo de la lista de exclusiones de la actuación en curso (cuando
 * el usuario vuelve a marcarlo o a elegir su conductor). La lista de otra
 * actuación no se toca.
 */
export const removeExcludedVehicle = (
  vehicleId: string,
  isValidation: boolean = false
): void => {
  const excluded = getExcludedVehicles(isValidation);
  if (!excluded.includes(vehicleId)) return;
  writeStored(isValidation, {
    actionId: currentActionId(isValidation),
    vehicleIds: excluded.filter((id) => id !== vehicleId),
  });
  log('vehículo %s vuelve a precargarse (%s)', vehicleId, isValidation ? 'validación' : 'nueva');
};

/**
 * Verificar si un vehículo está excluido en la actuación en curso
 */
export const isVehicleExcluded = (
  vehicleId: string,
  isValidation: boolean = false
): boolean => getExcludedVehicles(isValidation).includes(vehicleId);

/**
 * Limpiar todas las exclusiones del asistente. La llama el reset del asistente
 * de actuación nueva (`removeNewActionStorage`), para que una actuación que aún
 * no tiene id no herede la lista de otra que tampoco lo tenía.
 */
export const clearExcludedVehicles = (isValidation: boolean = false): void => {
  sessionStorage.removeItem(storageKeyFor(isValidation));
};
